import { defineStore } from 'pinia';
import { Router, useRoute, useRouter } from 'vue-router';
import { wxMethods } from '@/utils/wx-handle/wx-methods';
import { ReportType } from '@/hooks/types';
import { storage } from '@/utils/storage';
import Big from 'big.js';
import { appConfig } from '@/utils/config';
import { showToast } from 'vant';
import { encData } from '@/utils/http/axios';

// 当前系统，1：iOS 2：Android 3：H5 4：微信小程序
export enum OsVer {
  ios = 1,
  android = 2,
  h5 = 3,
  applet = 4,
}

export type OsVerKey = keyof typeof OsVer;
export type OsVerVal = `${OsVer}`;
type AbstractMethods = {
  toMyFeedback: () => void; // 跳转我的反馈
  toInviteSubMarkup: (options: { price: string | number }) => void; // 跳转下级加价
  toSharePage: (url: string) => void; // 分享页面
  checkReportAgain: () => void; // 继续查报告
  toMyReportPage: () => void; // 跳转我的报告
  toContactUs: () => void; // 跳转联系客服
  toMyPhoneService: () => void; //跳转广告客服
  tokenExpire: () => void; // token失效
  toVipCenter: () => void; // 跳转会员中心
  toVipPayBack: () => void; // vip支付跳首页
  toRegisterAgentSubmit: () => void; // 注册代理跳mine
  toSaveImg: () => void; // 物料长按保存图片
  toPayVipCenter: () => void; // 选择支付跳转会员中心
  toInviteAgents: () => void; // 邀请下级弹出框
  toBankCredit: () => void; // 征信分析，返回上级
  toCreditShare: () => void; // 征信分享
  shareTaxReceipts: () => void; // 分享税票
  refreshHome: () => void; // 领取会员之后 刷新 mine页面
  shareLetterSieve: () => void; // 百行信筛--生成海报
  getInvitePage: (options?: { reportType: number; path: string }) => void; // 邀请下级或生成报告海报
  toFinancingReport: () => void; // 生成融资企业报告海报
};
type GetCurEntity = (router: Router) => AbstractMethods;

// eslint-disable-next-line @typescript-eslint/no-empty-function
const defFn = () => {};

const getAndroidEntity: GetCurEntity = () => {
  // 调用Android方法
  const callAndroidHandle = (handleName: keyof AndroidPublicClass, option?: any) => {
    if (typeof CreditReportApp === 'undefined') return;
    const bridge = CreditReportApp as unknown as Record<
      keyof AndroidPublicClass,
      (option?: any) => unknown
    >;
    return option === undefined ? bridge[handleName]() : bridge[handleName](option);
  };

  return {
    toMyFeedback: () => callAndroidHandle('goFeedBack'),
    toInviteSubMarkup: ({ price }) => callAndroidHandle('toInviteSubMarkup', price),
    toSharePage: (url) => callAndroidHandle('goShareReport', url),
    checkReportAgain: () => callAndroidHandle('continueCheck'),
    toMyReportPage: () => callAndroidHandle('goMyReport'),
    toContactUs: () => callAndroidHandle('contactUs'),
    toMyPhoneService: () => callAndroidHandle('toMyPhoneService'),
    tokenExpire: () => callAndroidHandle('tokenExpire'),
    toVipCenter: () => callAndroidHandle('startVipCenter'),
    toVipPayBack: () => callAndroidHandle('vipPayBack'),
    toRegisterAgentSubmit: () => callAndroidHandle('registerAgentSubmit'),
    toSaveImg: () => callAndroidHandle('longPressSaveImage'),
    toPayVipCenter: () => callAndroidHandle('startVipCenter'),
    toInviteAgents: () => callAndroidHandle('subordinatePosterDialog'),
    toBankCredit: () => callAndroidHandle('creditAnalysisBack'),
    toCreditShare: () => callAndroidHandle('creditAnalysisShare'),
    shareTaxReceipts: () => callAndroidHandle('jellyfishShare'),
    refreshHome: () => callAndroidHandle('claimMembership'),
    shareLetterSieve: () => callAndroidHandle('hundredPosterShare'),
    getInvitePage: (options) => callAndroidHandle('toInviteSubordinates', options),
    toFinancingReport: () => callAndroidHandle('toFinancingReport'),
  };
};

const getIOSEntity: GetCurEntity = () => {
  // 调用IOS的方法
  const callIOSHandle = (handleName: keyof IOSPublicHandle, option?: any) => {
    window.webkit &&
      window.webkit.messageHandlers &&
      window.webkit.messageHandlers[handleName] &&
      window.webkit.messageHandlers[handleName].postMessage &&
      window.webkit.messageHandlers[handleName].postMessage(option || null);
  };

  return {
    toMyFeedback: () => callIOSHandle('goFeedBack'),
    toInviteSubMarkup: ({ price }) => callIOSHandle('toInviteSubMarkup', price),
    toSharePage: (url) => callIOSHandle('goShareReport', url),
    checkReportAgain: () => callIOSHandle('continueCheck'),
    toMyReportPage: () => callIOSHandle('goMyReport'),
    toContactUs: () => callIOSHandle('contactUs'),
    toMyPhoneService: () => callIOSHandle('toMyPhoneService'),
    tokenExpire: () => callIOSHandle('tokenExpire'),
    toVipCenter: () => callIOSHandle('startVipCenter'),
    toVipPayBack: () => callIOSHandle('vipPayBack'),
    toRegisterAgentSubmit: () => callIOSHandle('registerAgentSubmit'),
    toSaveImg: () => callIOSHandle('longPressSaveImage'),
    toPayVipCenter: () => callIOSHandle('startVipCenter'),
    toInviteAgents: () => callIOSHandle('subordinatePosterDialog'),
    toBankCredit: () => callIOSHandle('creditAnalysisBack'),
    toCreditShare: () => callIOSHandle('creditAnalysisShare'),
    shareTaxReceipts: () => callIOSHandle('jellyfishShare'),
    refreshHome: () => callIOSHandle('claimMembership'),
    shareLetterSieve: () => callIOSHandle('hundredPosterShare'),
    getInvitePage: (options) => callIOSHandle('toInviteSubordinates', options),
    toFinancingReport: () => callIOSHandle('toFinancingReport'),
  };
};

const getWechatMiniEntity: GetCurEntity = () => ({
  toMyFeedback: () => wxMethods.navigateTo({ url: '/pageMine/pages/myFeedback/myFeedback' }),
  toInviteSubMarkup: ({ price }) =>
    wxMethods.navigateTo({
      url: `/pageInvite/pages/inviteSubAgents/inviteSubAgents?price=${Big(price)
        .times(100)
        .toNumber()}`,
    }),
  toSharePage: defFn,
  checkReportAgain: defFn,
  toMyReportPage: defFn,
  toContactUs: defFn,
  toMyPhoneService: defFn,
  tokenExpire: defFn,
  toVipCenter: defFn,
  toVipPayBack: defFn,
  toRegisterAgentSubmit: defFn,
  toSaveImg: defFn,
  toPayVipCenter: defFn,
  toInviteAgents: defFn,
  toBankCredit: defFn,
  toCreditShare: defFn,
  shareTaxReceipts: defFn,
  refreshHome: defFn,
  shareLetterSieve: defFn,
  getInvitePage: defFn,
  toFinancingReport: defFn,
});

const getH5Entity: GetCurEntity = (router) => ({
  toMyFeedback: () => router.push({ path: '/myFeedback' }),
  toInviteSubMarkup: ({ price }) =>
    router.push({
      path: '/inviteSub',
      query: { price: price },
    }),
  toSharePage: defFn,
  checkReportAgain: defFn,
  toMyReportPage: defFn,
  toContactUs: defFn,
  toMyPhoneService: defFn,
  tokenExpire: defFn,
  toVipCenter: defFn,
  toVipPayBack: defFn,
  toRegisterAgentSubmit: defFn,
  toSaveImg: defFn,
  toPayVipCenter: defFn,
  toInviteAgents: defFn,
  toBankCredit: defFn,
  toCreditShare: defFn,
  shareTaxReceipts: defFn,
  refreshHome: defFn,
  shareLetterSieve: defFn,
  getInvitePage: defFn,
  toFinancingReport: defFn,
});

export const useAppOs = defineStore('useAppOs', () => {
  const route = useRoute();
  const router = useRouter();

  const storageOs = storage.getItem('osVersion', 2) as OsVerVal;
  const osVer = ref<OsVerKey>(storageOs ? (OsVer[storageOs] as OsVerKey) : 'h5');
  const setOsVer = (curOs: OsVerKey) => (osVer.value = curOs);
  // 是否是 android 或者 ios
  const isAppUse = computed(() => ['ios', 'android'].includes(osVer.value));

  // 调用Android方法
  const callAndroidHandle = (handleName: keyof AndroidPublicClass, option?: any) => {
    if (typeof CreditReportApp === 'undefined') return;
    const bridge = CreditReportApp as unknown as Record<
      keyof AndroidPublicClass,
      (option?: any) => unknown
    >;
    if (typeof bridge[handleName] !== 'function') return;
    // 必须在原生注入对象上直接调用，不能取出方法后独立执行，否则会丢失 Java 桥接接收者。
    return option === undefined ? bridge[handleName]() : bridge[handleName](option);
  };

  // 调用IOS的方法
  const callIOSHandle = (handleName: keyof IOSPublicHandle, option?: any) => {
    window.webkit &&
      window.webkit.messageHandlers &&
      window.webkit.messageHandlers[handleName] &&
      typeof window.webkit.messageHandlers[handleName].postMessage === 'function' &&
      window.webkit.messageHandlers[handleName].postMessage(option || null);
  };

  const toMyFeedback = () => {
    if (route.query.platform === 'applet') {
      wxMethods.navigateTo({ url: '/pageMine/pages/myFeedback/myFeedback' });
    } else if (osVer.value === 'android') {
      callAndroidHandle('goFeedBack');
    } else if (osVer.value === 'ios') {
      callIOSHandle('goFeedBack');
    } else {
      // router.push({ path: '/myFeedback' });
      location.href = appConfig.WechatService;
    }
  };

  const toMyPhoneService = () => {
    location.href = appConfig.WechatPhoneService;
  };

  // 返回工作台
  const toWorkBench = () => {
    if (route.query.platform === 'applet') {
      // wxMethods.navigateTo({ url: '/pageMine/pages/myFeedback/myFeedback' });
    } else if (osVer.value === 'android') {
      callAndroidHandle('toWorkBench');
    } else if (osVer.value === 'ios') {
      callIOSHandle('toWorkBench');
    } else {
      router.replace({ path: '/home' });
    }
  };

  // 下级加价
  const toInviteSubMarkup = (opt: { price: string }) => {
    if (route.query.platform === 'applet') {
      wxMethods.navigateTo({
        url: `/pageInvite/pages/inviteSubAgents/inviteSubAgents?price=${Big(opt.price)
          .times(100)
          .toNumber()}`,
      });
    } else if (osVer.value === 'android') {
      callAndroidHandle('toInviteSubMarkup', opt.price);
    } else if (osVer.value === 'ios') {
      callIOSHandle('toInviteSubMarkup', { price: opt.price });
    } else {
      router.push({
        path: '/inviteSub',
        query: { price: opt.price },
      });
    }
  };
  // token失效
  const tokenExpire = () => {
    if (osVer.value === 'android') {
      callAndroidHandle('tokenExpire');
    } else if (osVer.value === 'ios') {
      // console.log('share ios');
      callIOSHandle('tokenExpire');
    }
  };

  const toSharePage = (url: string) => {
    if (osVer.value === 'android') {
      callAndroidHandle('goShareReport', url);
    } else if (osVer.value === 'ios') {
      // console.log('share ios');
      callIOSHandle('goShareReport', { url });
    }
  };

  // 继续查看报告
  const checkReportAgain = () => {
    if (osVer.value === 'android') {
      callAndroidHandle('continueCheck');
    } else if (osVer.value === 'ios') {
      // console.log('share ios');
      callIOSHandle('continueCheck');
    }
  };

  const toMyReportPage = () => {
    if (osVer.value === 'android') {
      callAndroidHandle('goMyReport');
    } else if (osVer.value === 'ios') {
      // console.log('share ios');
      callIOSHandle('goMyReport');
    }
  };

  // 联系客服
  const toContactUs = () => {
    if (osVer.value === 'android') {
      callAndroidHandle('contactUs');
    }
  };

  // 跳转会员中心
  const toVipCenter = (linkId?: any, type?: string) => {
    if (route.query.platform === 'applet') {
      //
    } else if (osVer.value === 'android') {
      callAndroidHandle('startVipCenter', type);
    } else if (osVer.value === 'ios') {
      callIOSHandle('startVipCenter', { type });
    } else {
      router.push({
        path: '/vipCenter',
        query: {
          param: encData(
            JSON.stringify({
              linkId: linkId.id || '',
            })
          ),
        },
      });
    }
  };

  // 选择支付会员中心
  const toPayVipCenter = () => {
    if (route.query.platform === 'applet') {
      //
    } else if (osVer.value === 'android') {
      callAndroidHandle('vipPayBack');
    } else if (osVer.value === 'ios') {
      callIOSHandle('vipPayBack');
    } else {
      // router.back();
      router.push('/mine');
    }
  };
  // vip返回首页
  const toVipPayBack = () => {
    if (route.query.platform === 'applet') {
      //
    } else if (osVer.value === 'android') {
      callAndroidHandle('vipPayBack');
    } else if (osVer.value === 'ios') {
      callIOSHandle('vipPayBack');
    } else {
      const timeData = Date.now();
      window.location.href = `yxkcreditreport://yxkcreditreport/open?time=${timeData}`;
      console.log('有无跳转', `yxkcreditreport://yxkcreditreport/open?time=${timeData}`);
      let timer: any = null;
      // 1s之后跳转到APP下载页
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        router.replace({ path: '/appGuide' });
      });
    }
  };

  // 广告注册代理跳转mine页面
  const toRegisterAgentSubmit = () => {
    if (route.query.platform === 'applet') {
      //
    } else if (osVer.value === 'android') {
      callAndroidHandle('registerAgentSubmit');
    } else if (osVer.value === 'ios') {
      callIOSHandle('registerAgentSubmit');
    } else {
      router.replace({ path: '/mine' });
    }
  };
  // 保存图片
  const toSaveImg = (opt: { imageUrl?: string; codeUrl?: string }) => {
    console.log('进入方法', osVer.value);
    if (route.query.platform === 'applet') {
      //
    } else if (osVer.value === 'android') {
      console.log("osVer.value === 'android'", osVer.value);
      callAndroidHandle('longPressSaveImage', `${opt.imageUrl},${opt.codeUrl}`);
    } else if (osVer.value === 'ios') {
      console.log("osVer.value === 'ios'", osVer.value);
      callIOSHandle('longPressSaveImage', { ...opt });
    } else {
      console.log('h5', osVer.value);
      //
    }
  };

  // 跳转邀请代理
  const toInviteAgents = () => {
    if (route.query.platform === 'applet') {
      //
    } else if (osVer.value === 'android') {
      callAndroidHandle('subordinatePosterDialog');
    } else if (osVer.value === 'ios') {
      callIOSHandle('subordinatePosterDialog');
    } else {
      //
    }
  };

  type MonthlyActivityTaskHandle =
    | 'toInviteSubordinates'
    | 'toReportPromotion'
    | 'toProductCenter'
    | 'toInviteMember';

  // 月度推广活动任务跳转到 App 原生页面
  const toMonthlyActivityTask = (handleName: MonthlyActivityTaskHandle) => {
    const routePlatform = String(route.query.platform || '').toLowerCase();
    const androidBridge =
      typeof CreditReportApp === 'undefined'
        ? undefined
        : (CreditReportApp as unknown as Record<
            keyof AndroidPublicClass,
            (option?: any) => unknown
          >);
    const androidHandler = androidBridge?.[handleName];
    const iosHandler = window.webkit?.messageHandlers?.[handleName];

    // 原生实际注入的方法优先，避免 URL 或旧会话平台值导致 App 环境误判。
    if (typeof androidHandler === 'function') {
      callAndroidHandle(handleName);
      return;
    }
    if (typeof iosHandler?.postMessage === 'function') {
      callIOSHandle(handleName);
      return;
    }

    const platform = ['android', 'ios'].includes(routePlatform) ? routePlatform : osVer.value;
    if (platform === 'android') {
      callAndroidHandle(handleName);
    } else if (platform === 'ios') {
      callIOSHandle(handleName);
    }
  };

  // 征信报告返回跳转
  const toBankCredit = () => {
    if (route.query.platform === 'applet') {
      console.log('======>进入h5');
    } else if (osVer.value === 'android') {
      callAndroidHandle('creditAnalysisBack');
    } else if (osVer.value === 'ios') {
      callIOSHandle('creditAnalysisBack');
    } else {
      //
    }
  };

  // 征信分享
  const toCreditShare = (url: string) => {
    if (route.query.platform === 'applet') {
      console.log('======>进入h5');
    } else if (osVer.value === 'android') {
      console.log('======>进入android');
      callAndroidHandle('creditAnalysisShare', url);
    } else if (osVer.value === 'ios') {
      console.log('======>进入ios');
      callIOSHandle('creditAnalysisShare', { url });
    } else {
      //
    }
  };

  // 分享税票
  const shareTaxReceipts = (opt: { content?: string; url?: string }) => {
    console.log('进入方法', osVer.value);
    if (route.query.platform === 'applet') {
      //
    } else if (osVer.value === 'android') {
      callAndroidHandle(
        'jellyfishShare',
        JSON.stringify({
          content: opt.content,
          url: opt.url,
        })
      );
    } else if (osVer.value === 'ios') {
      callIOSHandle('jellyfishShare', { ...opt });
    } else {
      console.log('h5', osVer.value);
      //
    }
  };

  // 百信 生成推广海报
  const shareLetterSieve = (price: number) => {
    console.log('进入方法', osVer.value);
    if (route.query.platform === 'applet') {
      //
    } else if (osVer.value === 'android') {
      callAndroidHandle('hundredPosterShare', price);
    } else if (osVer.value === 'ios') {
      callIOSHandle('hundredPosterShare', { price });
    } else {
      console.log('h5', osVer.value);
      //
    }
  };

  // 领取会员刷新app mine页面
  const refreshHome = () => {
    if (route.query.platform === 'applet') {
      console.log('======>进入h5');
    } else if (osVer.value === 'android') {
      callAndroidHandle('claimMembership');
    } else if (osVer.value === 'ios') {
      callIOSHandle('claimMembership');
    } else {
      //
    }
  };
  // 百信邀请下级
  const getInvitePage = (options?: { reportType: number; path: string }) => {
    if (route.query.platform === 'applet') {
      console.log('======>进入h5');
    } else if (osVer.value === 'android') {
      callAndroidHandle('toInviteSubordinates', options);
    } else if (osVer.value === 'ios') {
      callIOSHandle('toInviteSubordinates', options);
    } else {
      //
    }
  };
  // 融资企业报告生成海报
  const toFinancingReport = () => {
    if (route.query.platform === 'applet') {
      console.log('======>进入h5');
    } else if (osVer.value === 'android') {
      callAndroidHandle('toFinancingReport');
    } else if (osVer.value === 'ios') {
      callIOSHandle('toFinancingReport');
    } else {
      //
    }
  };

  return {
    osVer,
    setOsVer,
    isAppUse,

    toMyPhoneService,
    toMyFeedback,
    toSharePage,
    checkReportAgain,
    toMyReportPage,
    toContactUs,
    toInviteSubMarkup,

    callAndroidHandle,
    callIOSHandle,
    toWorkBench,
    tokenExpire,
    toVipCenter,
    toVipPayBack,
    toRegisterAgentSubmit,
    toSaveImg,
    toPayVipCenter,
    toInviteAgents,
    toMonthlyActivityTask,
    toBankCredit,
    toCreditShare,
    shareTaxReceipts,
    refreshHome,
    shareLetterSieve,
    getInvitePage,
    toFinancingReport,
  };
});
